// File: /pages/ShuffleMaps/Favorites.jsx
"use client";

import React, { useEffect, useState } from "react";
import styles from "./Favorites.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";

export default function FavoritesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Normalize API rows -> props ListingCard expects
  const normalize = (row) => {
    // Prefer camelCase if present, else fallback to underscore fields from SQL
    const id = Number(row.id ?? row.listing_id);
    return {
      id,
      name: row.name,
      status: row.status,
      categories:
        Array.isArray(row.categories)
          ? row.categories
          : row.categories_csv
          ? String(row.categories_csv).split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      shortDescription: row.shortDescription ?? row.short_description ?? "",
      activeThumbnailUrl:
        row.activeThumbnailUrl ?? row.active_thumbnail_url ?? row.thumbnail_url ?? null,
    };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/shufflemaps/favorites/list", {
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) throw new Error(await r.text());

        const data = await r.json();
        // Accept either an array payload OR an object { items: [...] }
        const raw = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
        if (!cancelled) setItems(raw.map(normalize));
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load favorites");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Keep in sync if a heart is toggled elsewhere (event name: favorites-changed)
    const onFav = (e) => {
      const { listingId, favorited } = e.detail || {};
      const id = Number(listingId);
      if (!Number.isFinite(id)) return;
      setItems((prev) => (favorited ? prev : prev.filter((x) => Number(x.id) !== id)));
    };
    window.addEventListener("shufflemaps:favorites-changed", onFav);
    return () => {
      cancelled = true;
      window.removeEventListener("shufflemaps:favorites-changed", onFav);
    };
  }, []);

  const removeFavorite = async (listingId) => {
    try {
      const r = await fetch("/api/shufflemaps/favorites/toggle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ listingId }),
      });
      if (!r.ok) return;
      const { favorited } = await r.json();
      if (!favorited) {
        setItems((prev) => prev.filter((x) => Number(x.id) !== Number(listingId)));
        // emit the same event others listen to
        try {
          window.dispatchEvent(
            new CustomEvent("shufflemaps:favorites-changed", {
              detail: { listingId: Number(listingId), favorited: false },
            })
          );
        } catch {}
      }
    } catch {}
  };

  return (
    <main className={styles.smMain}>
      <section className={styles.smSection}>
        <h1 className={styles.smTitle}>Favorites</h1>
        <p className={styles.smSub}>Your saved ShuffleMaps listings.</p>

        {loading && <div className={styles.info}>Loading…</div>}
        {err && <div className={styles.error}>Error: {String(err)}</div>}

        {!loading && !err && (
          <>
            <div className={styles.count}>
              {items.length} favorite{items.length === 1 ? "" : "s"}
            </div>

            {items.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyTitle}>No favorites yet</div>
                <div className={styles.emptySub}>Tap the ♥ on any listing to save it here.</div>
              </div>
            ) : (
              <div className={styles.grid}>
                {items.map((it) => (
                  <div key={it.id} className={styles.cardWrap}>
                    <ListingCard
                      listingId={it.id}
                      name={it.name}
                      status={it.status}
                      category={it.categories}
                      shortDescription={it.shortDescription}
                      activeThumbnailUrl={it.activeThumbnailUrl}
                      suppressEmptyRatingDisplay={true}
                      isFavoriteInitial={true}
                      showStatus={false}
                      showFavoriteButton={false}  // ← hide the built-in heart to avoid double ring
                    />
                    <button
                      className={styles.favBtn}
                      title="Remove from favorites"
                      aria-label="Remove from favorites"
                      onClick={() => removeFavorite(it.id)}
                    >
                      ♥
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
